import { Schema } from "effect"
import { AgentKind, ModelChoice, ModelProvider, Owned, RecordMeta } from "../common"
import {
  AgentId,
  BranchName,
  CredentialId,
  Handle,
  IntegrationId,
  Markdown,
  Millis,
  PolicyId,
  ProjectId,
  RepoId,
  Sha256,
  Slug,
  UserId,
  WorkspaceId,
} from "../ids"
import { Requires } from "../placement"
import { AccessRequest } from "../runner"

// #region workspace
/** `viewer` reads and comments; it issues no plan, ledger, memory or run command (decision 0003 #7). */
export const MemberRole = Schema.Literals(["owner", "admin", "member", "viewer"])

/** Domain view of a Better Auth `member` row plus profile. Better Auth (D1) is the source of truth. */
export const Member = Schema.Struct({
  userId: UserId,
  role: MemberRole,
  displayName: Schema.String,
  handle: Handle, // @jannik
  avatarUrl: Schema.NullOr(Schema.String),
})

/** A repository known to the workspace. Runners register local clones of it (`minga repo add`); paths never leave the machine. */
export const Repo = Schema.Struct({
  id: RepoId,
  ...Owned,
  remote: Schema.String, // normalised: "github.com/<org>/dictly"
  defaultBranch: Schema.String,
  createdAt: Millis,
})

/** Metadata only. Secret material is encrypted at rest in the owning DO and decrypted only by the Agent DO
 * (platform mind) or the egress proxy (sandboxes). It never appears in an event, a snapshot or a job. */
export const ModelCredential = Schema.Struct({
  id: CredentialId,
  ...Owned,
  scope: Schema.TaggedUnion({ workspace: {}, user: { userId: UserId } }),
  provider: ModelProvider,
  kind: Schema.Literals(["api_key", "chatgpt_plan"]),
  label: Schema.String,
  hint: Schema.String, // e.g. "…a4f2"
  createdAt: Millis,
})

/** Workspace integration registry entry (Phase 2, tool gateway). Grants to areas live in the Project DO. */
export const Integration = Schema.Struct({
  id: IntegrationId,
  ...Owned,
  key: Slug, // "openseo", "appstoreconnect"
  transport: Schema.Literals(["http", "stdio"]),
  /** stdio servers run on a machine; `executor` routes their publish runs (e.g. asc-mcp needs the Mac). */
  executor: Schema.NullOr(Requires),
  tools: Schema.Array(
    Schema.Struct({
      name: Schema.String,
      class: Schema.Literals(["read", "write", "outward"]),
      /** Pinned description+schema hash. A change requires re-approval (rug-pull defence). */
      schemaHash: Sha256,
    }),
  ),
  createdAt: Millis,
})
// #endregion workspace

// #region project
export const Cadence = Schema.Struct({
  timezone: Schema.String, // IANA, e.g. "Europe/Berlin"
  digestCron: Schema.NullOr(Schema.String), // null = on request only
  weeklyReview: Schema.NullOr(Schema.Struct({ weekday: Schema.Int, hour: Schema.Int })),
  cycleWeeks: Schema.NullOr(Schema.Int),
})

/**
 * How a temporary worker of this project works (settings wave S9, §5.5): the preferred CLI, model and effort per CLI,
 * whether it works in the project's repo, and a narrowing request. How it acts on a machine is the runner profile's.
 */
export const WorkerPreset = Schema.Struct({
  key: Slug, // "default" always exists
  label: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(40)),
  /** Preferred CLI; null = any. */
  agent: Schema.NullOr(AgentKind),
  /** Used when the worker lands on a Claude profile. */
  claude: ModelChoice,
  /** Used when the worker lands on a Codex profile. */
  codex: ModelChoice,
  /** Narrowing only (`{mcp: "minga_only"}` = Minga's tools only). */
  access: AccessRequest,
  /** "none": no repo, no gh, a scratch directory and no PR (research, data). */
  repo: Schema.Literals(["project", "none"]),
})
export type WorkerPreset = typeof WorkerPreset.Type
export const WorkerSettings = Schema.Struct({
  presets: Schema.Array(WorkerPreset).check(Schema.isMaxLength(8)),
})
export type WorkerSettings = typeof WorkerSettings.Type

export const DEFAULT_WORKER_PRESET: WorkerPreset = {
  key: "default",
  label: "Default",
  agent: null,
  claude: { model: null, effort: null },
  codex: { model: null, effort: null },
  access: {},
  repo: "project",
}
export const NO_REPO_WORKER_PRESET: WorkerPreset = {
  key: "no-repo",
  label: "No repo: research, data",
  agent: null,
  claude: { model: null, effort: null },
  codex: { model: null, effort: null },
  access: {},
  repo: "none",
}
/** What a project without `workers` uses. */
export const DEFAULT_WORKER_SETTINGS: WorkerSettings = {
  presets: [DEFAULT_WORKER_PRESET, NO_REPO_WORKER_PRESET],
}

/** This project's base branch for one of its repos (absent = the registry's default branch). */
export const RepoBaseBranch = Schema.Struct({ repoId: RepoId, branch: BranchName })
export type RepoBaseBranch = typeof RepoBaseBranch.Type

export const Project = Schema.Struct({
  ...RecordMeta,
  id: ProjectId,
  workspaceId: WorkspaceId,
  slug: Slug,
  name: Schema.String,
  description: Markdown,
  status: Schema.Literals(["active", "paused", "archived"]),
  leadAgentId: Schema.NullOr(AgentId),
  repos: Schema.Array(RepoId),
  /** Planner rule: at most this many active initiatives in the project (default 3). */
  wip: Schema.Struct({ maxActiveInitiatives: Schema.Int }),
  cadence: Cadence,
  /** Project default policy; areas and agents inherit from it. */
  policyId: PolicyId,
  /** Settings wave: worker presets; absent = DEFAULT_WORKER_SETTINGS. */
  workers: Schema.optionalKey(WorkerSettings),
  /** Settings wave: per-repo base branches; a repo without an entry uses the registry's default branch. */
  baseBranches: Schema.optionalKey(Schema.Array(RepoBaseBranch)),
})
// #endregion project
